import { entries, isEmpty } from 'lodash'
import {
    DeviationAnalysisType,
    GroupedValuationDeviationOptions,
    TransformedDeviationSchema,
    ValuationDeviationOption,
    ValuationDeviationOptions,
} from './types'
import { Typography } from 'ComponentLibrary/src/piazza'
import { styled } from '@mui/material'
import { isKeyOf, LoadingSkeleton, MonetaryValue, Select, tokens } from 'ComponentLibrary'
import { DeviationAnalysisRowLabelKey, DeviationAnalysisSectionLabelKey } from './constants'
import { mapDeviationAnalysisSchemaToTable } from './mapDeviationAnalysisSchemaToTable'
import React, { Dispatch, SetStateAction } from 'react'
import { DeviationLabel } from './DeviationLabel'
import { DeviationValue } from './DeviationValue'
import { DeviationChangePercentage, DeviationChangeValue } from './DeviationChange'
import {
    mapGroupedValuationOptionsToSelectOptions,
    mapValuationOptionsToSelectOptions,
    ValuationOptionSelectCopy,
} from './mapValuationOptionsToSelectOptions'

export type DeviationAnalysisWidgetCopy = {
    sectionLabels: Record<DeviationAnalysisSectionLabelKey, string>
    rowLabels: Record<DeviationAnalysisRowLabelKey, string>
    compactComparisonLabel: string
    currentLabel: string
    changeLabel: string
    percentagePointUnit?: string
    yearUnitLabels?: { one: string; other: string }
    selectOptionLabels?: ValuationOptionSelectCopy
}

type DeviationAnalysisWidgetProps = {
    deviationAnalysis?: DeviationAnalysisType
    isDeviationAnalysisPending?: boolean
    baseValuation?: ValuationDeviationOption
    comparisonValuation?: ValuationDeviationOption
    setBaseValuationId: Dispatch<SetStateAction<string | undefined>>
    setComparisonValuationId: Dispatch<SetStateAction<string | undefined>>
    valuationOptions?: ValuationDeviationOptions
    groupedValuationOptions?: GroupedValuationDeviationOptions
    hasGroupedValuations?: boolean
    lockBaseValuation?: boolean
    lockBaseValuationTitle?: string
    lockComparisonValuation?: boolean
    hideComparisonIfSingleValuation?: boolean
    variant?: 'default' | 'compact'
    copy: DeviationAnalysisWidgetCopy
    locale?: string
}

export function DeviationAnalysisWidget({
    deviationAnalysis,
    isDeviationAnalysisPending,
    valuationOptions,
    groupedValuationOptions,
    hasGroupedValuations,
    setBaseValuationId,
    setComparisonValuationId,
    baseValuation,
    comparisonValuation,
    lockBaseValuation,
    lockBaseValuationTitle,
    lockComparisonValuation,
    hideComparisonIfSingleValuation,
    variant = 'default',
    copy,
    locale = 'de-DE',
}: DeviationAnalysisWidgetProps) {
    const transformedDeviationAnalysis = mapDeviationAnalysisSchemaToTable(deviationAnalysis)
    const selectOptions = mapValuationOptionsToSelectOptions({
        valuationOptions,
        lockedBaseValuationId: lockBaseValuation ? baseValuation?.value : undefined,
        lockBaseValuationTitle,
        locale,
        copy: copy.selectOptionLabels,
    })
    const groupedSelectOptions = mapGroupedValuationOptionsToSelectOptions({
        groupedValuationOptions,
        lockedBaseValuationId: lockBaseValuation ? baseValuation?.value : undefined,
        lockBaseValuationTitle,
        locale,
        copy: copy.selectOptionLabels,
    })

    const getDeviationTableRowLabel = (
        key: DeviationAnalysisRowLabelKey,
        value: TransformedDeviationSchema<MonetaryValue> & Partial<{ customLabel?: string }>
    ): string => {
        if (value && 'customLabel' in value && value.customLabel) {
            return value.customLabel
        }
        return copy.rowLabels[key]
    }

    const hideComparison = hideComparisonIfSingleValuation && valuationOptions?.length === 1
    const isCompact = variant === 'compact'
    const percentagePointUnit = copy.percentagePointUnit ?? 'BP'
    const yearUnitLabels = copy.yearUnitLabels ?? { one: 'Jahr', other: 'Jahre' }

    return (
        <DeviationAnalysisContainer $reducedView={hideComparison}>
            {entries(transformedDeviationAnalysis).map(([sectionKey, section], index) => {
                const isSectionDefined = isKeyOf(sectionKey, copy.sectionLabels) && !isEmpty(section)
                return isSectionDefined ? (
                    <GroupedRowsContainer key={sectionKey}>
                        <LabelCell
                            style={{
                                height: isCompact ? '30px' : '40px',
                                paddingLeft: isCompact ? '8px' : undefined,
                                ...(index === 0 ? HEADER_CELL_STYLING : {}),
                            }}
                        >
                            <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                                {copy.sectionLabels[sectionKey]}
                            </Typography>
                        </LabelCell>
                        {!hideComparison && (
                            <ComparisonValueCell style={{ overflow: 'auto', ...(index === 0 ? HEADER_CELL_STYLING : {}) }}>
                                {index === 0 && isCompact ? (
                                    <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                        {copy.compactComparisonLabel}
                                    </Typography>
                                ) : index === 0 ? (
                                    <Select
                                        options={!hasGroupedValuations ? selectOptions : undefined}
                                        groupedOptions={hasGroupedValuations ? groupedSelectOptions : undefined}
                                        onChange={(e) => setComparisonValuationId(e.target.value)}
                                        value={comparisonValuation?.value}
                                        formControlSx={lockComparisonValuation ? LOCKED_SELECT_STYLING : undefined}
                                        selectProps={{
                                            readOnly: lockComparisonValuation,
                                            SelectDisplayProps: {
                                                style: {
                                                    minHeight: '38px',
                                                    padding: lockComparisonValuation
                                                        ? '0'
                                                        : `0
                                                         20px
                                                          ${tokens.SizingSpacing050}
                                                           ${tokens.SizingSpacing100}`,
                                                },
                                            },
                                        }}
                                        fullWidth
                                    />
                                ) : null}
                            </ComparisonValueCell>
                        )}
                        <BaseValueCell style={{ overflow: 'auto', ...(index === 0 ? HEADER_CELL_STYLING : {}) }}>
                            {index === 0 && isCompact ? (
                                <Typography variant="headline5" color={tokens.ColorTextSubtle} noWrap>
                                    {copy.currentLabel}
                                </Typography>
                            ) : (
                                index === 0 && (
                                    <Select
                                        options={!hasGroupedValuations ? selectOptions : undefined}
                                        groupedOptions={hasGroupedValuations ? groupedSelectOptions : undefined}
                                        onChange={(e) => setBaseValuationId(e.target.value)}
                                        value={baseValuation?.value}
                                        formControlSx={lockBaseValuation ? LOCKED_SELECT_STYLING : undefined}
                                        selectProps={{
                                            readOnly: lockBaseValuation,
                                            SelectDisplayProps: {
                                                style: {
                                                    padding: lockBaseValuation
                                                        ? '0'
                                                        : `0 20px ${tokens.SizingSpacing025} ${tokens.SizingSpacing100}`,
                                                },
                                            },
                                        }}
                                        fullWidth
                                    />
                                )
                            )}
                        </BaseValueCell>
                        {!hideComparison && (
                            <DeviationValueCell style={{ ...(index === 0 ? HEADER_CELL_STYLING : {}) }}>
                                {index === 0 && (
                                    <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                        {copy.changeLabel}
                                    </Typography>
                                )}
                            </DeviationValueCell>
                        )}
                        {!hideComparison && <DeviationPercentageCell style={{ ...(index === 0 ? HEADER_CELL_STYLING : {}) }} />}
                        {entries(section).map(([key, value]) => {
                            const isRowDefined = value || isDeviationAnalysisPending
                            return isRowDefined ? (
                                <React.Fragment key={key}>
                                    <LabelCell sx={{ height: isCompact ? '30px' : '40px', paddingLeft: isCompact ? '8px' : undefined }}>
                                        <DeviationLabel
                                            label={isKeyOf(key, copy.rowLabels) ? getDeviationTableRowLabel(key, value) : key}
                                            deviationChange={value?.deviation}
                                            invertTrendColors={value?.isInvertedTrend}
                                        />
                                    </LabelCell>
                                    {!hideComparison && (
                                        <ComparisonValueCell sx={{ height: isCompact ? '30px' : '40px' }}>
                                            {isDeviationAnalysisPending ? (
                                                <LoadingSkeleton width="50%" />
                                            ) : (
                                                <DeviationValue
                                                    deviationValue={value?.comparisonValuation}
                                                    locale={locale}
                                                    yearUnitLabels={yearUnitLabels}
                                                />
                                            )}
                                        </ComparisonValueCell>
                                    )}
                                    <BaseValueCell sx={{ height: isCompact ? '30px' : '40px' }}>
                                        {isDeviationAnalysisPending ? (
                                            <LoadingSkeleton width="50%" />
                                        ) : (
                                            <DeviationValue
                                                deviationValue={value?.baseValue}
                                                bold={true}
                                                locale={locale}
                                                yearUnitLabels={yearUnitLabels}
                                            />
                                        )}
                                    </BaseValueCell>
                                    {!hideComparison && (
                                        <DeviationValueCell sx={{ height: isCompact ? '30px' : '40px' }}>
                                            {isDeviationAnalysisPending ? (
                                                <LoadingSkeleton width="50%" />
                                            ) : (
                                                <DeviationChangeValue
                                                    deviationChange={value?.deviation}
                                                    percentagePointUnit={percentagePointUnit}
                                                    locale={locale}
                                                    yearUnitLabels={yearUnitLabels}
                                                />
                                            )}
                                        </DeviationValueCell>
                                    )}
                                    {!hideComparison && (
                                        <DeviationPercentageCell sx={{ height: isCompact ? '30px' : '40px' }}>
                                            {isDeviationAnalysisPending ? (
                                                <LoadingSkeleton width="40%" />
                                            ) : (
                                                <DeviationChangePercentage
                                                    deviationChange={value?.deviation}
                                                    deviationPercentage={value?.deviationPercentage}
                                                    invertTrendColors={value?.isInvertedTrend}
                                                    percentagePointUnit={percentagePointUnit}
                                                    locale={locale}
                                                    yearUnitLabels={yearUnitLabels}
                                                />
                                            )}
                                        </DeviationPercentageCell>
                                    )}
                                </React.Fragment>
                            ) : null
                        })}
                    </GroupedRowsContainer>
                ) : null
            })}
        </DeviationAnalysisContainer>
    )
}

const LOCKED_SELECT_STYLING = {
    '& .MuiOutlinedInput-notchedOutline': {
        border: '0 !important',
    },
    '& .MuiSelect-icon': { display: 'none' },
    '& .MuiSelect-select': { cursor: 'unset' },
}

const DeviationAnalysisContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(({ $reducedView }: { $reducedView?: boolean }) => ({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: $reducedView ? '1fr 1fr' : '0.85fr 1.075fr 1.075fr 1fr minmax(100px, 0.6fr)',
    height: 'fit-content',
    position: 'sticky',
    top: tokens.SizingSpacing200,
}))

const GroupedRowsContainer = styled('div')(() => ({
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    '&:not(:last-of-type)': {
        borderBottom: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    },
}))

const GRID_CELL_STYLING = {
    alignItems: 'center',
    display: 'flex',
}

const HEADER_CELL_STYLING = {
    height: 'auto',
    paddingTop: tokens.SizingSpacing100,
}

const LabelCell = styled('div')({
    ...GRID_CELL_STYLING,
    paddingRight: tokens.SizingSpacing100,
})

const ComparisonValueCell = styled('div')({
    ...GRID_CELL_STYLING,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing100,
    justifyContent: 'flex-end',
})

const BaseValueCell = styled('div')({
    ...GRID_CELL_STYLING,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing100,
    backgroundColor: tokens.ColorBackgroundDark,
    justifyContent: 'flex-end',
})

const DeviationValueCell = styled('div')({
    ...GRID_CELL_STYLING,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing100,
    justifyContent: 'flex-end',
    textAlign: 'right',
})

const DeviationPercentageCell = styled('div')({
    ...GRID_CELL_STYLING,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing100,
})
